import { css, html, LitElement, nothing } from "lit";

import "../src/components/artifact-browser";
import "../src/components/change-password-form";
import "../src/components/login-form";
import { ltErrorText } from "../src/lib/errors-lt";
import {
  artifactPath,
  browsePath,
  canonicalPath,
  parseRoute,
  type Route,
} from "../src/lib/router";
import { controlStyles, documentBase, documentTokens } from "../src/styles/tokens";
import { AuthApi, type AuthUser } from "../src/transport/auth";
import { RestTransport } from "../src/transport/rest";
import { TransportError } from "../src/transport/types";

/**
 * Browser shell (spec-web-ui §3 entry/): login view + me-check on boot +
 * sign-out + change-password + the ~30-line history routing. This is the
 * COMPOSITION ROOT — the only place that knows the concrete transport
 * (RestTransport) and the auth API; everything below it is props-in/
 * events-out components.
 */

// Design tokens + base document look, once, at the document level —
// custom properties inherit through every shadow root (tokens.ts note).
document.adoptedStyleSheets = [
  ...document.adoptedStyleSheets,
  documentTokens.styleSheet!,
  documentBase.styleSheet!,
];

class ModulinaApp extends LitElement {
  static override properties = {
    authState: { state: true },
    user: { state: true },
    route: { state: true },
    loginBusy: { state: true },
    loginErrorCode: { state: true },
    sessionNote: { state: true },
    showChangePassword: { state: true },
    changeBusy: { state: true },
    changeErrorCode: { state: true },
    changeDone: { state: true },
  };

  declare authState: "checking" | "signed-out" | "signed-in";
  declare user: AuthUser | null;
  declare route: Route;
  declare loginBusy: boolean;
  declare loginErrorCode: string | null;
  /** Shown above the login form after a session expiry. */
  declare sessionNote: string;
  declare showChangePassword: boolean;
  declare changeBusy: boolean;
  declare changeErrorCode: string | null;
  declare changeDone: boolean;

  private readonly auth = new AuthApi();
  /** sessionAuthor (spec-web-ui §2: author = the session user, components
   * never ask): a thunk over the live me() state — display name first,
   * email as fallback. Since B-39 the SERVER derives the stored author
   * from the session user itself; this thunk only satisfies the wire
   * contract (author is a required metadata field) and remains the
   * caps.upload prerequisite. sessionScope (B-39 ride-along) feeds the
   * same caps: scope=read users get no upload affordance — the server's
   * insufficient_scope boundary stays the backstop. */
  private readonly transport = new RestTransport({
    sessionAuthor: () => this.user?.name || this.user?.email || null,
    sessionScope: () => this.user?.scope ?? null,
  });

  constructor() {
    super();
    this.authState = "checking";
    this.user = null;
    this.route = parseRoute(location.pathname);
    this.loginBusy = false;
    this.loginErrorCode = null;
    this.sessionNote = "";
    this.showChangePassword = false;
    this.changeBusy = false;
    this.changeErrorCode = null;
    this.changeDone = false;
  }

  static override styles = [
    controlStyles,
    css`
      :host {
        display: block;
        max-width: 1100px;
        margin: 0 auto;
        padding: var(--m-space-4);
      }
      header.app {
        display: flex;
        align-items: center;
        gap: var(--m-space-3);
        margin-bottom: var(--m-space-4);
      }
      header.app h1 {
        font-size: var(--m-fs-title);
        margin: 0;
      }
      .who {
        color: var(--m-color-text-muted);
        font-size: var(--m-fs-small);
      }
      .spacer {
        flex: 1;
      }
      .note {
        max-width: 22rem;
        margin: 0 auto var(--m-space-3);
        color: var(--m-color-danger);
        font-size: var(--m-fs-small);
        text-align: center;
      }
      .checking {
        color: var(--m-color-text-muted);
        text-align: center;
        margin-top: 20vh;
      }
      modulina-change-password {
        margin-bottom: var(--m-space-4);
      }
    `,
  ];

  // --------------------------------------------------------------------
  // History routing (the whole thing — spec §3 "~30 eilučių")
  // --------------------------------------------------------------------

  /** Parse the address bar and keep it TRUTHFUL: any pathname that is not
   * the route's canonical form (unknown path, trailing slash) is replaced
   * with the canonical one — the router docblock's promise. */
  private syncRouteFromLocation(): void {
    this.route = parseRoute(location.pathname);
    const canonical = canonicalPath(this.route);
    if (location.pathname !== canonical) history.replaceState(null, "", canonical);
  }

  private onPopState = (): void => {
    this.syncRouteFromLocation();
  };

  private navigate(path: string): void {
    if (location.pathname !== path) history.pushState(null, "", path);
    this.syncRouteFromLocation();
  }

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("popstate", this.onPopState);
    this.syncRouteFromLocation(); // initial URL gets the same truthfulness
    void this.boot();
  }

  override disconnectedCallback(): void {
    window.removeEventListener("popstate", this.onPopState);
    super.disconnectedCallback();
  }

  // --------------------------------------------------------------------
  // Auth flows
  // --------------------------------------------------------------------

  private async boot(): Promise<void> {
    try {
      const user = await this.auth.me();
      this.user = user;
      this.authState = user ? "signed-in" : "signed-out";
    } catch {
      // Server unreachable at boot — the login form will surface it on submit.
      this.authState = "signed-out";
    }
  }

  private async onSignIn(email: string, password: string): Promise<void> {
    this.loginBusy = true;
    this.loginErrorCode = null;
    try {
      this.user = await this.auth.signIn(email, password);
      this.authState = "signed-in";
      this.sessionNote = "";
    } catch (err) {
      this.loginErrorCode = err instanceof TransportError ? err.code : "internal_error";
    } finally {
      this.loginBusy = false;
    }
  }

  private async onSignOut(): Promise<void> {
    // The UI signs out either way; a sign-out that never reached the server
    // is surfaced honestly (the cookie stays HttpOnly-dead in this tab, but
    // the server session survives until TTL/revocation — review minor 3).
    let note = "";
    try {
      await this.auth.signOut();
    } catch {
      note =
        "Nepavyko susisiekti su serveriu atsijungiant — naršyklės sesija uždaryta, " +
        "bet serveryje ji galėjo likti galioti.";
    }
    this.user = null;
    this.authState = "signed-out";
    this.showChangePassword = false;
    this.sessionNote = note;
    this.navigate(browsePath());
  }

  private onSessionExpired = (): void => {
    this.user = null;
    this.authState = "signed-out";
    this.sessionNote = ltErrorText("unauthorized");
  };

  private async onChangePassword(currentPassword: string, newPassword: string): Promise<void> {
    this.changeBusy = true;
    this.changeErrorCode = null;
    this.changeDone = false;
    try {
      await this.auth.changePassword(currentPassword, newPassword);
      this.changeDone = true;
    } catch (err) {
      if (err instanceof TransportError && err.sessionExpired && err.code === "unauthorized") {
        this.onSessionExpired();
        return;
      }
      this.changeErrorCode = err instanceof TransportError ? err.code : "internal_error";
    } finally {
      this.changeBusy = false;
    }
  }

  // --------------------------------------------------------------------
  // Views
  // --------------------------------------------------------------------

  override render() {
    if (this.authState === "checking") {
      return html`<p class="checking" aria-live="polite">Kraunama…</p>`;
    }
    if (this.authState === "signed-out") {
      return html`
        ${this.sessionNote ? html`<p class="note" role="alert">${this.sessionNote}</p>` : nothing}
        <modulina-login
          .busy=${this.loginBusy}
          .errorCode=${this.loginErrorCode}
          @sign-in=${(e: CustomEvent<{ email: string; password: string }>) =>
            void this.onSignIn(e.detail.email, e.detail.password)}
        ></modulina-login>
      `;
    }
    return html`
      <header class="app">
        <h1>Modulina — failų saugykla</h1>
        <span class="spacer"></span>
        <span class="who">${this.user?.name} (${this.user?.email})</span>
        <button @click=${() => (this.showChangePassword = !this.showChangePassword)}>
          Keisti slaptažodį
        </button>
        <button @click=${() => void this.onSignOut()}>Atsijungti</button>
      </header>

      ${this.showChangePassword
        ? html`<modulina-change-password
            .busy=${this.changeBusy}
            .errorCode=${this.changeErrorCode}
            .done=${this.changeDone}
            @change-password=${(e: CustomEvent<{ currentPassword: string; newPassword: string }>) =>
              void this.onChangePassword(e.detail.currentPassword, e.detail.newPassword)}
          ></modulina-change-password>`
        : nothing}

      <modulina-artifact-browser
        .transport=${this.transport}
        .deepLinkArtifactId=${this.route.artifactId}
        .me=${this.user?.email ?? null}
        @artifact-opened=${(e: CustomEvent<{ id: string }>) =>
          this.navigate(artifactPath(e.detail.id))}
        @artifact-closed=${() => this.navigate(browsePath())}
        @session-expired=${this.onSessionExpired}
      ></modulina-artifact-browser>
    `;
  }
}

customElements.define("modulina-app", ModulinaApp);
